import React, { useEffect, useState, lazy, Suspense } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import ReactGA from 'react-ga4';
import { Routes as Router, Route } from 'react-router-dom';
import './styles/App.scss';

import PageLoader from './components/ui/PageLoader';
import useFeature from './hooks/useFeature';
import FAQs from './pages/FAQs';
import { useAppSelector, useAppDispatch } from './app/hooks';
import { login, User } from './app/slice/authSlice';
import VerificationPendingModal from './components/ui/VerificationPendingModal';
import toast from 'react-hot-toast';
import { trackPageView } from './utils/tracker';
import NewLanding from './pages/Landing/NewLanding';
import useIsPFC from './hooks/useIsPFC';
import useIsPFA from './hooks/useIsPFA';
import EmployerList from './pages/EmployerList';
import ProductList from './pages/ProductsList';
import Settings from './pages/Settings';
import { handleSelectEmployer } from './features/employer/employerSlice';
import { appDispatch } from './app/store';
import WorkflowUser from './pages/Auth/Workflows/WorkflowUsers';
import WorkflowAudit from './pages/Auth/Workflows/WorkflowAudits';
import PFCDetails from './pages/Pension/PFCDetails';
import CardPaymentTerms from './pages/CardPaymentTerms';
import ExcelValidator from './pages/Pension/ExcelValidator';
import ContributionTypesDetails from './pages/Pension/ContributionTypes';

// Lazy loaded components
const Login = lazy(() => import('./pages/Auth/Login'));
const SignUp = lazy(() => import('./pages/Auth/SignUp'));
const ForgotPassword = lazy(() => import('./pages/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/Auth/ResetPassword'));
const Profile = lazy(() => import('./pages/Auth/Profile'));
const Workflows = lazy(() => import('./pages/Auth/Workflows'));

const Dashboard = lazy(() => import('./pages/Pension/Dashboard'));
const DashboardHome = lazy(() => import('./pages/Generic/DashboardHome'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const Beneficiaries = lazy(() => import('./pages/Pension/Beneficiaries'));
const SingleBeneficiariesList = lazy(() => import('./pages/Pension/Beneficiaries/SingleBeneficiariesList'));
// const WalletPage = lazy(() => import('./pages/Pension/wallet'));
// const DirectDebitPage = lazy(() => import('./pages/Pension/DirectDebit'));
const PFCDashboardHome = lazy(() => import('./pages/Pension/PFCDashboardHome'));
const ScheduleItem = lazy(() => import('./pages/Pension/ScheduleItem'));
const HowItWorks = lazy(() => import('./pages/HowItWorks'));
const PFADetails = lazy(() => import('./pages/Pension/PFAContacts'));
const OverView = lazy(() => import('./pages/Pension/Overview'));
const Users = lazy(() => import('./pages/Generic/Users'));
const PaymentsDetails = lazy(() => import('./pages/Pension/PaymentDetails'));
const Custodians = lazy(() => import('./pages/Pension/Custodians'));
const Payments = lazy(() => import('./pages/Pension/Payments'));
const NotFound = lazy(() => import('./pages/404'));
const VerifyOTP = lazy(() => import('./pages/Auth/VerifyOTP'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const UserAgreement = lazy(() => import('./pages/UserAgreement'));
const PFADetailsItems = lazy(() => import('./pages/Pension/PaymentDetails/PFADetailsItems'));
const ProcessingPage = lazy(() => import('./pages/Pension/ProcessingPage'));
const NotificationsPage = lazy(() => import('./pages/Pension/Notifications'));

const SalaryPayment = lazy(() => import('./pages/Salary/SalaryPayments'));
const WalletPageSalary = lazy(() => import('./pages/Salary/walletSalary'));
const DirectDebitPageSalary = lazy(() => import('./pages/Salary/DirectDebitSalary'));
const SalaryDashboard = lazy(() => import('./pages/Salary/SalaryManagement'));
const BankLists = lazy(() => import('./pages/Salary/BankList'));
const SingleEmployeesList = lazy(() => import('./pages/Salary/Employees/SingleEmployeeList'));
const Employees = lazy(() => import('./pages/Salary/Employees'));
const PaymentsDetailsSalary = lazy(() => import('./pages/Salary/PaymentDetailsSalary'));
const OverViewSalary = lazy(() => import('./pages/Salary/Overview'));

function Routes() {
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const dispatch = useAppDispatch();
  const { showVerificationPending } = useAppSelector(state => state.auth);
  const location = useLocation();
  const isPFC = useIsPFC();
  const isPFA = useIsPFA();
  const HomeComponent = isPFC ? PFCDashboardHome : isPFA ? Custodians : DashboardHome;
  const showSalary = useFeature('salary');
  const routes = [
    {
      path: '/',
      Component: NewLanding,
      protected: false,
    },
    {
      path: '/login',
      Component: Login,
      protected: false,
    },
    {
      path: '/register',
      Component: SignUp,
      protected: false,
    },
    {
      path: '/faqs',
      Component: FAQs,
      protected: false,
    },
    {
      path: '/contact',
      Component: ContactUs,
      protected: false,
    },
    {
      path: '/how-it-works',
      Component: HowItWorks,
      protected: false,
    },
    {
      path: '/register/:referrerId',
      Component: SignUp,
      protected: false,
    },
    {
      path: '/forgot-password',
      Component: ForgotPassword,
      protected: false,
    },
    {
      path: '/verify',
      Component: VerifyOTP,
      protected: false,
    },
    {
      path: '/reset-password',
      Component: ResetPassword,
      protected: false,
    },
    {
      path: '/products',
      Component: ProductList,
      protected: true,
    },
    {
      path: '/pension/select_employer',
      Component: EmployerList,
      protected: true,
    },
    {
      path: '/card-terms',
      Component: CardPaymentTerms,
      protected: true,
    },
    {
      path: '/dashboard/',
      Component: Dashboard,
      protected: true,
      children: [
        {
          path: '/',
          Component: HomeComponent
        },
        {
          path: '/overview',
          Component: OverView
        },
        {
          path: '/contribution-types',
          Component: ContributionTypesDetails
        },
        {
          path: '/beneficiaries',
          Component: Beneficiaries,
        },
        {
          path: '/beneficiaries/:id',
          Component: SingleBeneficiariesList
        },
        {
          path: '/schedules',
          Component: Custodians,
        },
        {
          path: '/users',
          Component: Users,
        },
        {
          path: '/schedules/details/:id',
          Component: ScheduleItem,
        },
        {
          path: '/payments',
          Component: Payments,
        },
        {
          path: '/validator',
          Component: ExcelValidator,
        },
        {
          path: '/payments/details/:id',
          Component: PaymentsDetails,
        },
        {
          path: '/payments/details/pfa/:id',
          Component: PFADetailsItems,
        },
        {
          path: '/payments/processing/:referenceId',
          Component: ProcessingPage,
        },
        {
          path: '/notifications',
          Component: NotificationsPage,
        },
        {
          path: '/pfa-details',
          Component: PFADetails,
        },
        {
          path: '/pfc-details',
          Component: PFCDetails,
        },
        {
          path: '/profile',
          Component: Profile,
        },
        // {
        //   path: '/payment-methods/wallet',
        //   Component: WalletPage,
        // },
        // {
        //   path: '/payment-methods/direct-debit',
        //   Component: DirectDebitPage,
        // },
        {
          path: '/settings',
          Component: Settings,
        },
        {
          path: '/workflows',
          Component: Workflows,
        },
        {
          path: '/workflows/workflow/audit',
          Component: WorkflowAudit,
        },
        {
          path: '/workflows/workflow/workflow-users/:id',
          Component: WorkflowUser,
        },
      ]
    },
    ...(showSalary ? [{
      path: '/salary/',
      Component: SalaryDashboard,
      protected: true,
      children: [
        {
          path: '/',
          Component: HomeComponent
        },
        {
          path: '/overview',
          Component: OverViewSalary
        },
        {
          path: '/employees',
          Component: Employees,
        },
        {
          path: '/employees/:id',
          Component: SingleEmployeesList
        },
        {
          path: '/users',
          Component: Users,
        },
        {
          path: '/schedules/details/:id',
          Component: ScheduleItem,
        },
        {
          path: '/payments',
          Component: SalaryPayment,
        },
        {
          path: '/payments/details/:id',
          Component: PaymentsDetailsSalary,
        },
        {
          path: '/profile',
          Component: Profile,
        },
        {
          path: '/payment-methods/wallet',
          Component: WalletPageSalary,
        },
        {
          path: '/payment-methods/direct-debit',
          Component: DirectDebitPageSalary,
        },
        {
          path: '/payment-methods/banks',
          Component: BankLists,
        },
        {
          path: '/settings',
          Component: Settings,
        },
        {
          path: '/workflows',
          Component: Workflows,
        },
      ]
    }] : []),
    {
      path: '/terms',
      Component: Terms,
      protected: false,
    },
    {
      path: '/privacy',
      Component: Privacy,
      protected: false,
    },
    {
      path: '/user-agreement',
      Component: UserAgreement,
      protected: false,
    },
  ];

  useEffect(() => {
    if (process.env.REACT_APP_ENABLED_FEATURES !== 'development') {
      if (isAuthenticated && user) {
        ReactGA.initialize(
          process.env.REACT_APP_GA_ID as string,
          {
            gaOptions: {
              userId: user.id?.toString() || user.email,
            }
          }
        );
      } else {
        ReactGA.initialize(process.env.REACT_APP_GA_ID as string);
      }
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    let token = localStorage.getItem('tazat');
    let penUser = localStorage.getItem("PenUser");
    const getProfile = async () => {
      if (token) {
        try {
          // Token injection is handled by the lib/axios.ts request interceptor.
          // We still decode the JWT here to populate the Redux auth state.
          const profile = jwtDecode(token);
          dispatch(login({ ...profile as unknown as User, defaultEmployerCode: penUser ? JSON.parse(penUser).defaultEmployerCode : null }));
          setLoading(false);

          const selectedEmployer = localStorage.getItem('employer');
          if (selectedEmployer) {
            appDispatch(handleSelectEmployer(JSON.parse(selectedEmployer)))
          }
        } catch (error) {
          // @ts-ignore
          if (error?.message === 'Network Error') {
            toast.error('Network Error, check your internet and refresh the page.')
          }
          // A malformed/expired token must not leave the app stuck on the loading screen;
          // unauthenticated users will be redirected to login by the route guard.
          setLoading(false);
        }
      }
    };
    if (token) {
      getProfile();
    } else {
      setLoading(false)
    }
  }, [dispatch]);

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  if (loading) {
    return <PageLoader />
  }

  return (
    <div className="App">
      <Router>
        {
          routes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                <AuthGuard
                  isProtected={route.protected}
                  route={route.path}
                >
                  <Suspense fallback={<PageLoader />}>
                    <route.Component />
                  </Suspense>
                </AuthGuard>
              }
            >
              {
                route.children && route.children.map((childRoute, index) => {
                  if (index === 0) {
                    return (
                      <Route
                        index
                        key={childRoute.path}
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <childRoute.Component />
                          </Suspense>
                        }
                      />
                    )
                  }
                  return (
                    <Route
                      key={childRoute.path}
                      path={`${route.path}/${childRoute.path}`}
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <childRoute.Component />
                        </Suspense>
                      }
                    />
                  )
                })
              }
            </Route>
          ))
        }
        <Route path='*' element={
          <Suspense fallback={<PageLoader />}>
            <NotFound />
          </Suspense>
        } />
      </Router>
      <VerificationPendingModal
        isOpen={showVerificationPending}
      />
    </div>
  );
}

export default Routes;

const AuthGuard = ({ isProtected, children, route }: { isProtected: boolean, children: React.ReactElement, route: string }) => {
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const userType = user?.usertype;
  const isPersonalRoute = route.split('/')[1] === 'dashboard';
  const isAdminRoute = route.split('/')[1] === 'admin';

  useEffect(() => {
    if (isProtected && !isAuthenticated) {
      return navigate("/login");
    }
    if ((!isProtected && isAuthenticated)) {
      return navigate("/products/");
    }
  }, [isProtected, isAuthenticated, navigate, userType, isPersonalRoute, isAdminRoute]);

  return children
}
